import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { MenuItem } from '@mui/material';

import t from 'utils/translate';
import SelectInput from 'modules/shared/components/select-input';
import LabelWithInfo from 'modules/shared/components/label-with-info';
import { selectCompanySettings, selectDataLoaded } from 'modules/settings/stores/company-settings-slice-handler';
import {
    selectSettingsJson,
    toggleCrossMuteListDialogOpen,
    selectCrossMuteListDialogOpen,
    updateJsonSetting,
} from 'modules/settings/stores/user-settings-slice-handler';
import { USER_SETTINGS_OPTIONS } from 'constants/user-settings';
import ActiveInactiveSelector from './active-inactive-selector';
import LabelWithCheckbox from 'modules/shared/components/label-with-checkbox';
import { DefaultPageMapping } from '../../../constants/top-bar-constants';
import Button from '@mui/material/Button';
import CrossMuteListDialog from './cross-mute-list-dialog';

export default function SectionOptions() {
    const companySettings = useSelector(selectCompanySettings);
    const settingsJson = useSelector(selectSettingsJson);
    const dataLoaded = useSelector(selectDataLoaded);
    const crossMuteListDialogOpen = useSelector(selectCrossMuteListDialogOpen);

    const [settings, updateSettings] = useState({
        defaultPage: settingsJson.defaultPage,
        defaultSendButton: settingsJson.defaultSendButton,
        defaultAction: settingsJson.defaultAction,
        defaultContactsPageTab: settingsJson.defaultContactsPageTab,
        walkieTalkieTakeFloorMode: settingsJson.walkieTalkieTakeFloorMode,
        liveReplay: settingsJson.liveReplay,
        channelsPlaybackMode: settingsJson.channelsPlaybackMode,
        hearOtherChannelsWhileTalking: settingsJson.hearOtherChannelsWhileTalking,
        channelsConfirmationWindowDisplay: settingsJson.channelsConfirmationWindowDisplay,
        channelsAdvancedMode: settingsJson.channelsAdvancedMode,
        channelsMultipleTalkingEnabled: settingsJson.channelsMultipleTalkingEnabled,
        conversationWithOnDutyContacts: settingsJson.conversationWithOnDutyContacts,
        sortContactsBy: settingsJson.sortContactsBy,
    });

    useEffect(() => {
        updateSettings({
            defaultPage: settingsJson.defaultPage,
            defaultSendButton: settingsJson.defaultSendButton,
            defaultAction: settingsJson.defaultAction,
            defaultContactsPageTab: settingsJson.defaultContactsPageTab,
            walkieTalkieTakeFloorMode: settingsJson.walkieTalkieTakeFloorMode,
            liveReplay: settingsJson.liveReplay,
            channelsPlaybackMode: settingsJson.channelsPlaybackMode,
            hearOtherChannelsWhileTalking: settingsJson.hearOtherChannelsWhileTalking,
            channelsConfirmationWindowDisplay: settingsJson.channelsConfirmationWindowDisplay,
            channelsAdvancedMode: settingsJson.channelsAdvancedMode,
            channelsMultipleTalkingEnabled: settingsJson.channelsMultipleTalkingEnabled,
            conversationWithOnDutyContacts: settingsJson.conversationWithOnDutyContacts,
            sortContactsBy: settingsJson.sortContactsBy,
        });
    }, [settingsJson]);

    const notificationMessages = {
        defaultPage: t('js_all_settings_default_page_changed_successfully'),
        defaultSendButton: t('js_all_settings_default_send_button_successfully'),
        defaultAction: t('js_all_settings_default_action_successfully'),
        defaultContactsPageTab: t('js_all_settings_default_contacts_tab_successfully'),
        walkieTalkieTakeFloorMode: t('js_all_settings_take_floor_mode_successfully'),
        liveReplay: t('js_all_live_replay_changed_successfully'),
        channelsPlaybackMode: t('js_all_settings_channels_playback_mode_changed_successfully'),
        hearOtherChannelsWhileTalking: t('js_all_settings_channels_hear_all_successfully_changed'),
        channelsConfirmationWindowDisplay: t('settings_confirmation_window_display_success'),
        channelsAdvancedMode: t('settings_channels_display_mode_notification_success'),
        channelsMultipleTalkingEnabled: t('settings_channels_multiple_talking_notification_success'),
        conversationWithOnDutyContacts: t(
            'js_all_user_availability_conversation_available_contact_successfully_changed',
        ),
        sortContactsBy: t('js_all_sort_contacts_by_successfully_changed'),
    };

    const handleChange = async (field, value) => {
        updateSettings({ ...settings, [field]: value });
        await updateJsonSetting(field, value, t('settings_options'), notificationMessages[field]);
    };

    /**
     * @param {string} field
     * @param {SyntheticBaseEvent} e
     */
    const handleChangeChannelsConfirmationWindowDisplay = async (field, e) => {
        let newOptions = [...settingsJson.channelsConfirmationWindowDisplay];

        if (e.target.checked) {
            newOptions.push(field);
        } else {
            newOptions = newOptions.filter((option) => option !== field);
        }

        await handleChange('channelsConfirmationWindowDisplay', newOptions);
    };

    if (!dataLoaded) {
        return null;
    }

    const defaultPage =
        typeof DefaultPageMapping[settings.defaultPage] !== 'undefined'
            ? DefaultPageMapping[settings.defaultPage]
            : settings.defaultPage;

    return (
        <section className="settings-section settings-options">
            <div className="settings-section-title">
                <LabelWithInfo text={t('settings_options')} />
                <div className="strike" />
            </div>
            <div className="section-content">
                <div className="section-row">
                    <LabelWithInfo text={t('settings_default_page_label')} info={t('settings_default_page_info')} />
                    <SelectInput
                        className="info-field-dropdown"
                        value={defaultPage}
                        data-test-id="default-page"
                        handleChange={(e) => handleChange('defaultPage', e.target.value)}
                    >
                        <MenuItem value={USER_SETTINGS_OPTIONS.PAGES.CONTACTS}>
                            {t('settings_default_page_contacts')}
                        </MenuItem>
                        <MenuItem value={USER_SETTINGS_OPTIONS.PAGES.CONVERSATIONS}>
                            {t('settings_default_page_conversations')}
                        </MenuItem>
                        {companySettings.AllowWalkieTalkie && (
                            <MenuItem value={USER_SETTINGS_OPTIONS.PAGES.CHANNELS}>
                                {t('settings_default_page_channels')}
                            </MenuItem>
                        )}
                        {companySettings.IsDispatcher && (
                            <MenuItem value={USER_SETTINGS_OPTIONS.PAGES.GEOLOCATION}>
                                {t('settings_default_page_geolocation')}
                            </MenuItem>
                        )}
                        {companySettings.AllowMyBusiness && (
                            <MenuItem value={USER_SETTINGS_OPTIONS.PAGES.MY_BUSINESS}>
                                {t('settings_default_page_mybusiness')}
                            </MenuItem>
                        )}
                    </SelectInput>
                </div>
                <div className="section-row">
                    <LabelWithInfo
                        text={t('settings_default_contacts_tab_label')}
                        info={t('settings_default_contacts_tab_info')}
                    />
                    <SelectInput
                        className="info-field-dropdown"
                        value={settings.defaultContactsPageTab}
                        data-test-id="default-contacts-page-tab"
                        handleChange={(e) => handleChange('defaultContactsPageTab', e.target.value)}
                    >
                        <MenuItem value={USER_SETTINGS_OPTIONS.CONTACTS_PAGE_TABS.USERS}>{t('js_all_users')}</MenuItem>
                        <MenuItem value={USER_SETTINGS_OPTIONS.CONTACTS_PAGE_TABS.GROUPS}>
                            {t('js_all_groups')}
                        </MenuItem>
                    </SelectInput>
                </div>
                {(companySettings.AllowVoip || companySettings.AllowWalkieTalkie) && (
                    <div className="section-row">
                        <LabelWithInfo
                            text={t('settings_default_action')}
                            info={t(
                                companySettings.AllowVoip
                                    ? 'settings_default_action_tooltip'
                                    : 'settings_default_action_tooltip_calls_disabled',
                            )}
                        />
                        <SelectInput
                            className="info-field-dropdown"
                            value={settings.defaultAction}
                            data-test-id="default-action"
                            handleChange={(e) => handleChange('defaultAction', e.target.value)}
                        >
                            <MenuItem value={USER_SETTINGS_OPTIONS.ACTIONS.MESSAGE}>
                                {t('settings_default_action_message')}
                            </MenuItem>
                            {companySettings.AllowVoip && (
                                <MenuItem value={USER_SETTINGS_OPTIONS.ACTIONS.CALL}>
                                    {t('settings_default_action_call')}
                                </MenuItem>
                            )}
                            {companySettings.AllowWalkieTalkie && (
                                <MenuItem value={USER_SETTINGS_OPTIONS.ACTIONS.PTT}>
                                    {t('settings_default_action_ptt')}
                                </MenuItem>
                            )}
                        </SelectInput>
                    </div>
                )}
                {companySettings.AllowMessageRequiresAck && (
                    <div className="section-row">
                        <LabelWithInfo text={t('settings_send_button_title')} info={t('settings_send_button_info')} />
                        <SelectInput
                            className="info-field-dropdown"
                            value={settings.defaultSendButton}
                            data-test-id="default-send-button"
                            handleChange={(e) => handleChange('defaultSendButton', e.target.value)}
                        >
                            <MenuItem value={USER_SETTINGS_OPTIONS.SEND_BUTTONS.SEND}>
                                {t('settings_send_button_action_send')}
                            </MenuItem>
                            <MenuItem value={USER_SETTINGS_OPTIONS.SEND_BUTTONS.OK_SEND}>
                                {t('settings_send_button_action_ok_send')}
                            </MenuItem>
                        </SelectInput>
                    </div>
                )}
                {companySettings.AllowWalkieTalkie && (
                    <>
                        <div className="section-row">
                            <LabelWithInfo
                                text={t('settings_walkie_talkie_take_floor_mode')}
                                info={t('settings_walkie_talkie_take_floor_mode_tooltip')}
                            />
                            <SelectInput
                                className="info-field-dropdown"
                                value={settings.walkieTalkieTakeFloorMode}
                                data-test-id="walkie-talkie-take-floor-mode"
                                handleChange={(e) => handleChange('walkieTalkieTakeFloorMode', e.target.value)}
                            >
                                <MenuItem value={USER_SETTINGS_OPTIONS.PTT_TAKE_FLOOR_MODE.PTT_MODE}>
                                    {t('setting_ptt_take_floor_mode')}
                                </MenuItem>
                                <MenuItem value={USER_SETTINGS_OPTIONS.PTT_TAKE_FLOOR_MODE.TOGGLE}>
                                    {t('setting_ptt_toggle_mode')}
                                </MenuItem>
                                <MenuItem value={USER_SETTINGS_OPTIONS.PTT_TAKE_FLOOR_MODE.BOTH}>
                                    {t('setting_ptt_both')}
                                </MenuItem>
                            </SelectInput>
                        </div>
                        {companySettings.AllowLiveReplay && (
                            <div className="section-row">
                                <LabelWithInfo
                                    text={t('settings_live_replay')}
                                    info={t('settings_live_replay_tooltip')}
                                />
                                <ActiveInactiveSelector
                                    initialValue={settings.liveReplay}
                                    data-test-id="live-replay"
                                    onChange={(v) => handleChange('liveReplay', v)}
                                />
                            </div>
                        )}
                        <div className="section-row">
                            <LabelWithInfo
                                text={t('settings_channels_playback_mode')}
                                info={t('settings_channels_playback_mode_tooltip')}
                            />
                            <SelectInput
                                className="info-field-dropdown"
                                value={settings.channelsPlaybackMode}
                                data-test-id="channels-playback-mode"
                                handleChange={(e) => handleChange('channelsPlaybackMode', e.target.value)}
                            >
                                <MenuItem value={USER_SETTINGS_OPTIONS.CHANNELS_PLAYBACK_MODE.HEAR_ALL}>
                                    {t('settings_channels_playback_mode_hear_all')}
                                </MenuItem>
                                <MenuItem value={USER_SETTINGS_OPTIONS.CHANNELS_PLAYBACK_MODE.HEAR_FIRST_SPEAKER}>
                                    {t('settings_channels_playback_mode_hear_first_speaker')}
                                </MenuItem>
                                {companySettings.PriorityMaxLevels && (
                                    <MenuItem value={USER_SETTINGS_OPTIONS.CHANNELS_PLAYBACK_MODE.HEAR_FIRST_PRIORITY}>
                                        {t('settings_channels_playback_mode_hear_first_priority')}
                                    </MenuItem>
                                )}
                            </SelectInput>
                        </div>
                        <div className="section-row">
                            <LabelWithInfo
                                text={t('settings_channels_hear_others_while_talking_label')}
                                info={t('settings_channels_hear_others_while_talking_tooltip')}
                            />
                            <ActiveInactiveSelector
                                initialValue={settings.hearOtherChannelsWhileTalking}
                                data-test-id="hear-other-channels-while-talking"
                                onChange={(v) => handleChange('hearOtherChannelsWhileTalking', v)}
                            />
                        </div>
                        {companySettings.IsDispatcher && (
                            <div className="section-row" data-test-id={`cross-mute-list-row`}>
                                <LabelWithInfo
                                    text={t('settings_cross_mute_list_label')}
                                    info={t('settings_cross_mute_list_tooltip')}
                                    tooltipPlacement="right"
                                />
                                <Button
                                    variant="contained"
                                    role="primary"
                                    onClick={() => toggleCrossMuteListDialogOpen(true)}
                                >
                                    {t('settings_title')}
                                </Button>
                                {crossMuteListDialogOpen && <CrossMuteListDialog />}
                            </div>
                        )}
                    </>
                )}
                {(companySettings.AllowVoip || companySettings.AllowWalkieTalkie) && (
                    <div className="section-row">
                        <LabelWithInfo
                            text={t('settings_confirmation_window_display')}
                            info={t('settings_confirmation_window_display_info')}
                        />
                        <SelectInput
                            className="info-field-dropdown"
                            value={[-1]}
                            data-test-id="channels-confirmation-window-display"
                            multiple
                        >
                            <MenuItem value={-1}>{t('settings_select_option')}</MenuItem>
                            {companySettings.AllowWalkieTalkie && (
                                <MenuItem
                                    value={USER_SETTINGS_OPTIONS.CHANNELS_CONFIRMATION_WINDOW_DISPLAY.JOIN_CHANNEL}
                                >
                                    <LabelWithCheckbox
                                        labelContent={t('settings_channel_join_title')}
                                        checked={settings.channelsConfirmationWindowDisplay.includes(
                                            USER_SETTINGS_OPTIONS.CHANNELS_CONFIRMATION_WINDOW_DISPLAY.JOIN_CHANNEL,
                                        )}
                                        onChange={(e) =>
                                            handleChangeChannelsConfirmationWindowDisplay(
                                                USER_SETTINGS_OPTIONS.CHANNELS_CONFIRMATION_WINDOW_DISPLAY.JOIN_CHANNEL,
                                                e,
                                            )
                                        }
                                    />
                                </MenuItem>
                            )}
                            {companySettings.AllowWalkieTalkie && (
                                <MenuItem
                                    value={USER_SETTINGS_OPTIONS.CHANNELS_CONFIRMATION_WINDOW_DISPLAY.LEAVE_CHANNEL}
                                >
                                    <LabelWithCheckbox
                                        labelContent={t('settings_channel_leave_title')}
                                        checked={settings.channelsConfirmationWindowDisplay.includes(
                                            USER_SETTINGS_OPTIONS.CHANNELS_CONFIRMATION_WINDOW_DISPLAY.LEAVE_CHANNEL,
                                        )}
                                        onChange={(e) =>
                                            handleChangeChannelsConfirmationWindowDisplay(
                                                USER_SETTINGS_OPTIONS.CHANNELS_CONFIRMATION_WINDOW_DISPLAY
                                                    .LEAVE_CHANNEL,
                                                e,
                                            )
                                        }
                                    />
                                </MenuItem>
                            )}
                            {companySettings.AllowVoip && (
                                <MenuItem
                                    value={USER_SETTINGS_OPTIONS.CHANNELS_CONFIRMATION_WINDOW_DISPLAY.EXIT_CALL_FORWARD}
                                >
                                    <LabelWithCheckbox
                                        labelContent={t('settings_call_forward_exit')}
                                        checked={settings.channelsConfirmationWindowDisplay.includes(
                                            USER_SETTINGS_OPTIONS.CHANNELS_CONFIRMATION_WINDOW_DISPLAY
                                                .EXIT_CALL_FORWARD,
                                        )}
                                        onChange={(e) =>
                                            handleChangeChannelsConfirmationWindowDisplay(
                                                USER_SETTINGS_OPTIONS.CHANNELS_CONFIRMATION_WINDOW_DISPLAY
                                                    .EXIT_CALL_FORWARD,
                                                e,
                                            )
                                        }
                                    />
                                </MenuItem>
                            )}
                            {companySettings.AllowVoip && (
                                <MenuItem
                                    value={
                                        USER_SETTINGS_OPTIONS.CHANNELS_CONFIRMATION_WINDOW_DISPLAY.OVERRIDE_CALL_FORWARD
                                    }
                                >
                                    <LabelWithCheckbox
                                        labelContent={t('settings_call_forward_override')}
                                        checked={settings.channelsConfirmationWindowDisplay.includes(
                                            USER_SETTINGS_OPTIONS.CHANNELS_CONFIRMATION_WINDOW_DISPLAY
                                                .OVERRIDE_CALL_FORWARD,
                                        )}
                                        onChange={(e) =>
                                            handleChangeChannelsConfirmationWindowDisplay(
                                                USER_SETTINGS_OPTIONS.CHANNELS_CONFIRMATION_WINDOW_DISPLAY
                                                    .OVERRIDE_CALL_FORWARD,
                                                e,
                                            )
                                        }
                                    />
                                </MenuItem>
                            )}
                        </SelectInput>
                    </div>
                )}
                {companySettings.AllowWalkieTalkie && (
                    <>
                        <div className="section-row">
                            <LabelWithInfo
                                text={t('settings_channels_display_mode_label')}
                                info={t('settings_channels_display_mode_tooltip')}
                            />
                            <SelectInput
                                className="info-field-dropdown"
                                value={settings.channelsAdvancedMode}
                                data-test-id="channels-advanced-mode"
                                handleChange={(e) => handleChange('channelsAdvancedMode', e.target.value)}
                            >
                                <MenuItem value={USER_SETTINGS_OPTIONS.CHANNELS_ADVANCED_MODE.STANDARD}>
                                    {t('settings_channels_display_mode_standard')}
                                </MenuItem>
                                <MenuItem value={USER_SETTINGS_OPTIONS.CHANNELS_ADVANCED_MODE.ADVANCED}>
                                    {t('settings_channels_display_mode_advanced')}
                                </MenuItem>
                            </SelectInput>
                        </div>
                        <div className="section-row">
                            <LabelWithInfo
                                text={t('settings_channels_multiple_talking_label')}
                                info={t('settings_channels_multiple_talking_tooltip')}
                            />
                            <ActiveInactiveSelector
                                initialValue={settings.channelsMultipleTalkingEnabled}
                                data-test-id="channels-multiple-talking-enabled"
                                onChange={(v) => handleChange('channelsMultipleTalkingEnabled', v)}
                            />
                        </div>
                    </>
                )}
                {companySettings.AllowUserAvailability && (
                    <div className="section-row">
                        <LabelWithInfo
                            text={t('js_all_user_availability_conversation_available_contact')}
                            info={t('js_all_user_availability_ask_confirm_action')}
                        />
                        <ActiveInactiveSelector
                            initialValue={settings.conversationWithOnDutyContacts}
                            data-test-id="conversation-with-on-duty-contacts"
                            onChange={(v) => handleChange('conversationWithOnDutyContacts', v)}
                        />
                    </div>
                )}
                <div className="section-row">
                    <LabelWithInfo text={t('js_all_sort_contacts_by')} info={t('js_all_sort_contacts_by_tooltip')} />
                    <SelectInput
                        className="info-field-dropdown"
                        value={settings.sortContactsBy}
                        data-test-id="sort-contacts-by"
                        handleChange={(e) => handleChange('sortContactsBy', e.target.value)}
                    >
                        <MenuItem value={USER_SETTINGS_OPTIONS.SORT_CONTACTS_BY.FIRST_NAME}>
                            {t('sort_contacts_by_fn')}
                        </MenuItem>
                        <MenuItem value={USER_SETTINGS_OPTIONS.SORT_CONTACTS_BY.LAST_NAME}>
                            {t('sort_contacts_by_ln')}
                        </MenuItem>
                    </SelectInput>
                </div>
            </div>
        </section>
    );
}
